import { Box, Modal } from '@mui/material';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { ModalCloseReason } from '../BDDialog/BDDialog.model';
import {
  CLOSE_THRESHOLD,
  KEYBOARD_STEP,
  MAX_HEIGHT,
  MIN_HEIGHT,
  SNAP_POINTS,
} from './BDBottomSheet.constants';
import useStyles from './BDBottomSheet.styles';

interface BDBottomSheetProps {
  open: boolean;
  onClose: (reason?: ModalCloseReason) => void;
  children: ReactNode;
  showBackdrop?: boolean;
  ariaLabels?: {
    panel?: string;
    dragHandle?: string;
  };
  /**
   * Callback fired after the bottom sheet has exited.
   * Called when the sheet closes (after open changes from true to false).
   */
  onExited?: () => void;
}

/**
 * A draggable bottom sheet component that supports:
 * - Drag to resize via handle
 * - Snap points at 30%, 60%, 95% of viewport height
 * - Drag to dismiss when below 20% viewport height
 * - Keyboard navigation (Arrow keys, Home, End, Escape)
 * - Touch and mouse support
 */
const BDBottomSheet = ({
  open,
  onClose,
  children,
  showBackdrop = true,
  ariaLabels,
  onExited,
}: BDBottomSheetProps) => {
  const { classes, cx } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const ariaPanel = ariaLabels?.panel;
  const ariaDragHandle = ariaLabels?.dragHandle;
  const [heightPercent, setHeightPercent] = useState(MAX_HEIGHT);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  const wasOpen = useRef(open);

  useEffect(() => {
    if (open) {
      // Ensures modal height gets properly reset when modal (re)opens
      setHeightPercent(MAX_HEIGHT);
    } else if (wasOpen.current && !open) {
      // Sheet just closed - call onExited callback
      onExited?.();
    }
    wasOpen.current = open;
  }, [open, onExited]);

  /** Find the closest snap point to the current height */
  const findClosestSnapPoint = useCallback((height: number): number => {
    let closest = SNAP_POINTS[0];
    let minDist = Math.abs(height - closest);

    for (const point of SNAP_POINTS) {
      const dist = Math.abs(height - point);
      if (dist < minDist) {
        minDist = dist;
        closest = point;
      }
    }

    return closest;
  }, []);

  /** Handle drag start (mouse or touch) */
  const handleDragStart = useCallback(
    (clientY: number) => {
      setIsDragging(true);
      dragStartY.current = clientY;
      dragStartHeight.current = heightPercent;
    },
    [heightPercent]
  );

  /** Handle drag move */
  const handleDragMove = useCallback(
    (clientY: number) => {
      if (!isDragging) return;

      const viewportHeight = window.innerHeight;
      const deltaY = dragStartY.current - clientY;
      const deltaPercent = deltaY / viewportHeight;
      // Allow dragging below MIN_HEIGHT to reach CLOSE_THRESHOLD
      const newHeight = Math.min(
        MAX_HEIGHT,
        Math.max(0, dragStartHeight.current + deltaPercent)
      );

      setHeightPercent(newHeight);
    },
    [isDragging]
  );

  /** Handle drag end */
  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    // Close if below threshold
    if (heightPercent < CLOSE_THRESHOLD) {
      onClose('dragDismiss');
      return;
    }

    // Snap to closest point
    const snapPoint = findClosestSnapPoint(heightPercent);
    setHeightPercent(snapPoint);
  }, [isDragging, heightPercent, onClose, findClosestSnapPoint]);

  /** Mouse event handlers */
  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      handleDragStart(e.clientY);
    },
    [handleDragStart]
  );

  /** Touch event handlers */
  const handleTouchStart = useCallback(
    (e: ReactTouchEvent) => {
      const touch = e.touches[0];
      handleDragStart(touch.clientY);
    },
    [handleDragStart]
  );

  /** Keyboard navigation */
  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose('escapeKeyDown');
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const newHeight = Math.min(MAX_HEIGHT, heightPercent + KEYBOARD_STEP);
        setHeightPercent(newHeight);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const newHeight = Math.max(MIN_HEIGHT, heightPercent - KEYBOARD_STEP);
        if (newHeight < CLOSE_THRESHOLD) {
          onClose();
        } else {
          setHeightPercent(newHeight);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        setHeightPercent(MAX_HEIGHT);
      } else if (e.key === 'End') {
        e.preventDefault();
        setHeightPercent(MIN_HEIGHT);
      }
    },
    [heightPercent, onClose]
  );

  // Global event listeners for drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientY);
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      handleDragMove(touch.clientY);
    };

    const handleTouchEnd = () => {
      handleDragEnd();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  if (!open) {
    return null;
  }

  const heightPercentValue = Math.round(heightPercent * 100);

  return (
    <Modal
      open={open}
      onClose={(_e, reason) => onClose(reason)}
      hideBackdrop={!showBackdrop}
      disableScrollLock={false}
      slotProps={{
        ...(showBackdrop && {
          backdrop: {
            className: classes.backdrop,
          },
        }),
      }}
    >
      <Box
        ref={containerRef}
        className={cx(
          classes.container,
          isDragging && classes.containerDragging
        )}
        style={{ height: `${heightPercentValue}vh` }}
        role="dialog"
        aria-modal="true"
        aria-label={ariaPanel}
        data-testid="bd-bottom-sheet"
      >
        <Box
          className={classes.handle}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onKeyDown={handleKeyDown}
          role="slider"
          aria-label={ariaDragHandle}
          aria-valuemin={MIN_HEIGHT * 100}
          aria-valuemax={MAX_HEIGHT * 100}
          aria-valuenow={heightPercentValue}
          aria-valuetext={`Panel height: ${heightPercentValue}%`}
          tabIndex={0}
          data-testid="bottom-sheet-handle"
        >
          <Box className={classes.handleBar} />
        </Box>
        <Box className={classes.content}>{children}</Box>
      </Box>
    </Modal>
  );
};

export default BDBottomSheet;
